import { push } from 'connected-react-router';
import { batch } from 'react-redux';
import { checkIsAuthenticated } from 'functions/checkIsAuthenticated';
import { getUserConditionProfile, getUserWixId } from 'modules/user/utils';
import { getGeneralDiseaseSubType } from 'modules/user/conditionProfileHelpers';
import { PLATFORM, USER_SOURCE, USER_TYPES } from 'utils/constants/user';
import { GENERAL_DISEASE } from 'utils/constants/disease';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import { registerUser } from 'new-ui/functions';
import { getTargetUserProfile } from 'modules/user/api';
import {
  getFinanceParams,
  isFinanceProfileEmpty,
  preparePatientFinanceProfileToUpsert,
} from 'modules/financeSupport/utils';
import { upsertUserPatient } from 'components/Authentication/actions';
import { resetCurrentUser, updateCurrentUser } from 'modules/currentUser/actions';
import { selectPublicFinance } from 'modules/financeSupport/selectPublicFinance';
import { createFinanceProfileAction } from 'modules/financeSupport/actions';
import app from 'new-ui/app';
import { POPUP } from 'utils/constants/popup';
import { hcpApi } from '../api';
import { getCaregiverNewPatientEmail } from '../helpers';
import { savePatientPendingDocuments } from './savePatientPendingDocuments';

const GA = {
  category: 'HCP',
  events: {
    error: 'Could not signup',
  },
};

export const addPatientPopup = ({
  redirectPath, preserveCurrentUser, onSuccessAction,
} = {}) => (dispatch, getState) => {
  const {
    profile: { data: profile },
    currentUser: { data: currentUser },
    authentication,
  } = getState();

  if (!checkIsAuthenticated({ authentication, profile }) || !currentUser) {
    // invariant
    return;
  }

  const {
    personal: { condition, condition_name: conditionName } = {},
    condition_profile: conditionProfile,
  } = currentUser;

  if (!condition) {
    // invariant
    return;
  }

  const indication = {
    value: condition,
    title: conditionName ?? '',
    type: condition === GENERAL_DISEASE ? getGeneralDiseaseSubType(conditionProfile) : null,
  };

  app.openPopup(POPUP.ADD_HCP_PATIENT, {
    theme: 'middle',
    indication,
    onSuccess: async (user) => {
      const { currentUser: { pendingDocuments } } = getState();

      dispatch(updateCurrentUser({ user, dirty: false }));

      if (pendingDocuments.length) {
        await dispatch(savePatientPendingDocuments());
      }

      app.closePopups();
      batch(() => {
        dispatch(upsertUserPatient(user));
        if (!preserveCurrentUser) {
          dispatch(resetCurrentUser({}));
        }
        dispatch(
          onSuccessAction ? onSuccessAction() : push(redirectPath ?? ROUTE_PATH[ROUTE_NAME.PATIENTS]),
        );
      });
    },
    isFirstPatient: !profile?.patients?.length,
  });
};

// indication, indicationType (passed as parameters) are ignored
export const addPatient = ({
  email, name, onError, recaptchaToken, onSuccess,
}) => async (dispatch, getState) => {
  const {
    profile: { data: profile },
    currentUser: { data: currentUser },
    authentication,
  } = getState();

  if (!checkIsAuthenticated({ authentication, profile }) || !currentUser) {
    // invariant
    return;
  }

  app.showPreloader();

  const {
    personal: { condition } = {},
  } = currentUser;

  const finalEmail = email || getCaregiverNewPatientEmail(getUserWixId(profile));

  let patient;
  try {
    const { success, data: registrationResult } = await registerUser({
      email: finalEmail,
      indication: condition,
      recaptchaToken,
      condition_profile: getUserConditionProfile(currentUser),
      info: {
        ...currentUser.info,
        family_name: name,
        email: finalEmail,
        user_type: USER_TYPES.PATIENT,
        platform: PLATFORM.HCP,
        organization: profile.info?.organization,
        registered_by: getUserWixId(profile),
      },
    });
    if (!success) {
      throw new Error('Something went wrong');
    }
    const { accessToken } = registrationResult;
    patient = await getTargetUserProfile({ accessToken });

    const patientUserWixId = getUserWixId(patient);

    app.intercom.updateUserAttributes({
      email,
      condition,
      user_type: USER_TYPES.PATIENT,
      user_source: USER_SOURCE.HCP,
      shadow_patient: true,
    }, patientUserWixId);

    const addPatientResults = await hcpApi.addPatient(patientUserWixId);
    if (addPatientResults?.success !== true) {
      throw new Error('Something went wrong');
    }

    const { financeProfile: { data: publicFinanceProfile } = {} } = selectPublicFinance(getState());
    if (!isFinanceProfileEmpty(publicFinanceProfile)) {
      const { aidTypes } = publicFinanceProfile;
      const patientFinanceParams = preparePatientFinanceProfileToUpsert(getFinanceParams(patient, publicFinanceProfile, aidTypes));
      dispatch(createFinanceProfileAction(
        { userWixId: getUserWixId(patient), isHCP: true, financeProfile: patientFinanceParams },
      ));
    }
  } catch (ex) {
    app.sendGoogleAnalyticsEvent(GA.category, GA.events.error, { name: 'caregiver_patient_signup_error' });
    onError(ex);
    return;
  } finally {
    app.hidePreloader();
  }

  if (onSuccess) {
    onSuccess(patient);
  }
};
